import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Button, Pagination } from 'react-bootstrap';
import { FaHistory } from "react-icons/fa";
import { TbLoader3 } from "react-icons/tb"; // Importing the loader icon
import EditSalaryLayouts from '../../components/EditSalaryLayout.jsx';
import PFlayout from '../../components/PFLayouts.jsx';
import './pf-history.css';

// Dummy data for demonstration
const mockTableData = Array.from({ length: 30 }, (_, index) => ({
  id: index + 1,
  name: `Employee ${index + 1}`,
  role: `Role ${index + 1}`,
  description: `Description ${index + 1}`,
  date: `2024-09-${(index % 30) + 1}`
}));

export default function PFHistory() {
  const rowsPerPage = 10;
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [showTable, setShowTable] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false); // Define isGenerating state
  const [reportGenerated, setReportGenerated] = useState(false); // New state to track report generation

  const validateForm = () => {
    const errors = {};

    if (!startDate) {
      errors.startDate = 'Start date is required.';
    }

    if (!endDate) {
      errors.endDate = 'End date is required.';
    }

    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      errors.endDate = 'End date must be after start date.';
    }

    return errors;
  };

  const handleGenerateReport = () => {
    const errors = validateForm();

    if (Object.keys(errors).length === 0) {
      setFormErrors({});
      setIsGenerating(true); // Set generating state to true

      // Simulate report generation delay
      setTimeout(() => {
        setData(mockTableData); // Load data after delay
        setShowTable(true); // Show table after loading data
        setIsGenerating(false); // Reset generating state
        setReportGenerated(true); // Mark report as generated
      }, 2000); // Adjust time as needed
    } else {
      setFormErrors(errors);
      setShowTable(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= Math.ceil(mockTableData.length / rowsPerPage)) {
      setCurrentPage(pageNumber);
    }
  };

  useEffect(() => {
    if (showTable) {
      const startIndex = (currentPage - 1) * rowsPerPage;
      const endIndex = startIndex + rowsPerPage;
      setData(mockTableData.slice(startIndex, endIndex));
    }
  }, [currentPage, showTable]);

  const pageRange = () => {
    const pages = [];
    const totalPages = Math.ceil(mockTableData.length / rowsPerPage);
    let startPage = Math.max(currentPage - 1, 1);
    let endPage = Math.min(startPage + 2, totalPages);

    if (endPage - startPage < 2) {
      startPage = Math.max(endPage - 2, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const handleExportToExcel = () => {
    // Implement the logic to export the table data to Excel
  };

  const handleExportToPDF = () => {
    // Implement the logic to export the table data to PDF
  };

  return (
    <EditSalaryLayouts>
      <PFlayout>
        <div>
          <h1 className="provident-fund-history-header">
            <FaHistory /> Provident Fund History
          </h1>
          <hr className='provident-divider' />

          <Form className="provident-fund-history-form">
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label htmlFor="start_date">
                    Start date <span className="required">*</span> :
                  </Form.Label>
                  <Form.Control
                    type="date"
                    id="start_date"
                    name="start_date"
                    required
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    isInvalid={!!formErrors.startDate}
                  />
                  <Form.Control.Feedback type="invalid">{formErrors.startDate}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label htmlFor="end_date">
                    End date <span className="required">*</span> :
                  </Form.Label>
                  <Form.Control
                    type="date"
                    id="end_date"
                    name="end_date"
                    required
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    isInvalid={!!formErrors.endDate}
                  />
                  <Form.Control.Feedback type="invalid">{formErrors.endDate}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Button
              className="generate-report-button"
              type="button"
              onClick={handleGenerateReport}
              disabled={isGenerating || reportGenerated} // Disable button if generating or report is already generated
            >
              {isGenerating ? (
                <>
                  <TbLoader3 className="icon-rotate" />
                  Generating...
                </>
              ) : (
                'Generate Report'
              )}
            </Button>
          </Form>

          {showTable && (
            <div className="provident-fund-history-table-container">
              <div className="table-actions">
                <Row className="w-100">
                  <Col xs={12} md={6} className="d-flex align-items-center mb-3 justify-content-start">
                    <Button variant="secondary" className="export-button" onClick={handleExportToExcel}>Excel</Button>
                    <Button variant="secondary" className="export-button" onClick={handleExportToPDF}>PDF</Button>
                  </Col>
                  <Col xs={12} md={6} className="d-flex align-items-center mb-3 justify-content-end">
                    <Form className="d-flex justify-content-end">
                      <Form.Control
                        type="search"
                        placeholder="Search"
                        aria-label="Search"
                        className="search-bar"
                      />
                    </Form>
                  </Col>
                </Row>
              </div>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead className="provident-history-table-header">
                    <tr className='pf-history-header'>
                      <th>SR</th>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Description</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.length > 0 ? (
                      data.map((item, index) => (
                        <tr key={item.id}>
                          <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                          <td>{item.name}</td>
                          <td>{item.role}</td>
                          <td>{item.description}</td>
                          <td>{item.date}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">No data Available Now</td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className='pf-history-footer'>
                    <tr className="provident-history-table-footer">
                      <td>Total</td>
                      <td>0</td>
                      <td>0</td>
                      <td>0</td>
                      <td>0</td>
                    </tr>
                  </tfoot>
                </table>

                <div className="pagination-container">
                  <Row className="custom-row">
                    <Col xs={12} md={9} className="custom-col-left">
                      Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, mockTableData.length)} of {mockTableData.length} entries
                    </Col>
                    <Col xs={12} md={3} className="custom-col-right">
                      <nav>
                        <Pagination>
                          <Pagination.Prev
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(currentPage - 1)}
                          >
                            Previous
                          </Pagination.Prev>
                          {pageRange().map(pageNumber => (
                            <Pagination.Item
                              key={pageNumber}
                              active={currentPage === pageNumber}
                              onClick={() => handlePageChange(pageNumber)}
                            >
                              {pageNumber}
                            </Pagination.Item>
                          ))}
                          <Pagination.Next
                            disabled={currentPage === Math.ceil(mockTableData.length / rowsPerPage)}
                            onClick={() => handlePageChange(currentPage + 1)}
                          >
                            Next
                          </Pagination.Next>
                        </Pagination>
                      </nav>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          )}
        </div>
      </PFlayout>
    </EditSalaryLayouts>
  );
}
